// Detail operativy: hlavicka se stavem/akcemi, poznamka (inline edit),
// tabulka polozek s pridanim / mazanim / otevrenim detailu.
//
// Workflow akce (Odeslat ke schvaleni / Schvalit / Zamitnout / Otevrit k uprave / Uzavrit den)
// jsou napojeny na backend; gating tlacitek dle role + stavu resi `lib/permissions.ts`.

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  CheckCircle2,
  Eye,
  FileText,
  Lock,
  Loader2,
  Pencil,
  Plus,
  RotateCcw,
  Send,
  ShieldCheck,
  Trash2,
  XCircle,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { usePermissions } from '@/lib/permissions';
import { formatDateCs, formatDateTimeCs } from '@/lib/utils';
import type {
  Cinnost,
  CreatePolozkaInput,
  Mechanizace,
  OperativaDetail,
  Polozka,
  SchvaleniRadekDto,
  UpdatePolozkaInput,
  UzivatelVyber,
} from '@/types/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  RichText,
  RichTextView,
  jeRichPrazdny,
  richBlokSNazvem,
  richKUlozeni,
  richPripoj,
  richToPlain,
} from '@/components/ui/rich-text';
import { useToast } from '@/components/ui/use-toast';
import { StavBadge } from '@/components/StavBadge';
import { PolozkaDetailDialog } from '@/components/PolozkaDetailDialog';
import { ZamestnanciMultiSelect } from '@/components/ZamestnanciMultiSelect';

type PolozkaDialogState =
  | { mode: 'closed' }
  | { mode: 'view'; polozkaId: string }
  | { mode: 'create' }
  | { mode: 'edit'; item: Polozka }
  | { mode: 'delete'; item: Polozka };

// Truncate v Reactu (ne pres CSS overflow) — kvuli SR/copy-paste.
// Formatovana pole zkracujeme jako cisty text, at se do bunky nedostanou tagy.
function truncate(value: string | null | undefined, max: number): string {
  if (value == null) return '';
  const trimmed = richToPlain(value).trim();
  if (trimmed.length <= max) return trimmed;
  return trimmed.slice(0, max).trimEnd() + '…';
}

// Souhrn materialu pro tooltip (prvni 3 polozky).
function materialTooltip(material: { co: string; kolik: string | null }[]): string {
  if (material.length === 0) return '';
  const head = material.slice(0, 3).map((m) => (m.kolik ? `${m.co} ${m.kolik}` : m.co));
  const suffix = material.length > 3 ? `, …(+${material.length - 3})` : '';
  return head.join(', ') + suffix;
}

export function OperativaDetailPage() {
  const params = useParams<{ id: string }>();
  const id = params.id ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const perms = usePermissions();

  const detailKey = useMemo(() => ['operativa', id] as const, [id]);

  const detailQuery = useQuery<OperativaDetail>({
    queryKey: detailKey,
    queryFn: () => api<OperativaDetail>(`/operativy/${id}`),
    enabled: id !== '',
  });

  const op = detailQuery.data;

  // --- Poznamka inline edit ---
  const [poznamkaEditing, setPoznamkaEditing] = useState(false);
  const [poznamkaDraft, setPoznamkaDraft] = useState('');
  useEffect(() => {
    if (op) setPoznamkaDraft(op.poznamka ?? '');
  }, [op?.id, op?.poznamka]);

  const poznamkaMutation = useMutation({
    mutationFn: (poznamka: string | null) =>
      api<void>(`/operativy/${id}/poznamka`, {
        method: 'PUT',
        body: JSON.stringify({ poznamka }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: detailKey });
      toast({ title: 'Poznámka uložena' });
      setPoznamkaEditing(false);
    },
    onError: (err: Error) =>
      toast({ title: 'Uložení selhalo', description: err.message, variant: 'destructive' }),
  });

  // --- Smazani operativy ---
  const deleteOpMutation = useMutation({
    mutationFn: () => api<void>(`/operativy/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operativy'] });
      toast({ title: 'Operativa smazána' });
      navigate('/operativy', { replace: true });
    },
    onError: (err: Error) => {
      if (err instanceof ApiError && err.status === 409) {
        toast({
          title: 'Operativu nelze smazat',
          description: 'V jejím aktuálním stavu už mazání není povoleno.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Smazání selhalo',
          description: err.message,
          variant: 'destructive',
        });
      }
    },
  });
  const [confirmDelete, setConfirmDelete] = useState(false);

  // --- Workflow akce ---
  // POST /api/operativy/{id}/odeslat-ke-schvaleni  (VEDOUCI/ADMIN, stav NAVRH/UPRAVENA)
  const odeslatMutation = useMutation({
    mutationFn: () =>
      api<void>(`/operativy/${id}/odeslat-ke-schvaleni`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: detailKey });
      queryClient.invalidateQueries({ queryKey: ['operativy'] });
      toast({ title: 'Odesláno ke schválení' });
    },
    onError: (err: Error) =>
      toast({ title: 'Odeslání selhalo', description: err.message, variant: 'destructive' }),
  });

  // POST /api/operativy/{id}/otevrit-k-uprave  (VEDOUCI/ADMIN, stav KE_SCHVALENI/SCHVALENO/V_REALIZACI/UZAVRENO)
  const otevritMutation = useMutation({
    mutationFn: () =>
      api<void>(`/operativy/${id}/otevrit-k-uprave`, {
        method: 'POST',
        body: JSON.stringify({ duvod: null }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: detailKey });
      queryClient.invalidateQueries({ queryKey: ['operativy'] });
      toast({ title: 'Otevřeno k úpravě' });
    },
    onError: (err: Error) =>
      toast({ title: 'Otevření selhalo', description: err.message, variant: 'destructive' }),
  });

  // POST /api/operativy/{id}/uzavrit  (VEDOUCI/ADMIN, stav V_REALIZACI)
  const uzavritMutation = useMutation({
    mutationFn: () => api<void>(`/operativy/${id}/uzavrit`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: detailKey });
      queryClient.invalidateQueries({ queryKey: ['operativy'] });
      toast({ title: 'Den uzavřen' });
    },
    onError: (err: Error) =>
      toast({ title: 'Uzavření selhalo', description: err.message, variant: 'destructive' }),
  });

  // GET seznam Schvaleni radku pro operativu (vsem prihlasenym, kdy jsou videt + komu se nabidne akce).
  const schvaleniKey = useMemo(() => ['schvaleni', id] as const, [id]);
  const schvaleniQuery = useQuery<SchvaleniRadekDto[]>({
    queryKey: schvaleniKey,
    queryFn: () => api<SchvaleniRadekDto[]>(`/schvaleni/operativa/${id}`),
    // Schvaleni radky existuji az od stavu KE_SCHVALENI.
    enabled: id !== '',
  });

  // POST /api/operativy/{id}/schvalit  { schvalovatelId, ucastniciIds }
  // Schvaleni jde pres dialog, ve kterem schvalovatel vybere ucastniky operativy
  // (tisknou se na konci PDF). Vyber je nepovinny.
  const [schvalitState, setSchvalitState] = useState<{
    schvalovatelId: string;
    vybrani: string[];
  } | null>(null);

  // Lehky seznam uzivatelu pro vyber ucastniku — nacita se az pri otevreni dialogu.
  const uzivateleVyberQuery = useQuery<UzivatelVyber[]>({
    queryKey: ['uzivatele-vyber'],
    queryFn: () => api<UzivatelVyber[]>('/uzivatele/vyber'),
    enabled: schvalitState !== null,
  });

  const schvalitMutation = useMutation({
    mutationFn: ({ schvalovatelId, ucastniciIds }: { schvalovatelId: string; ucastniciIds: string[] }) =>
      api<void>(`/operativy/${id}/schvalit`, {
        method: 'POST',
        body: JSON.stringify({ schvalovatelId, ucastniciIds }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: detailKey });
      queryClient.invalidateQueries({ queryKey: schvaleniKey });
      queryClient.invalidateQueries({ queryKey: ['operativy'] });
      toast({ title: 'Schváleno' });
      setSchvalitState(null);
    },
    onError: (err: Error) =>
      toast({ title: 'Schválení selhalo', description: err.message, variant: 'destructive' }),
  });

  const [zamitState, setZamitState] = useState<{ schvalovatelId: string; duvod: string } | null>(null);
  const zamitnoutMutation = useMutation({
    mutationFn: ({ schvalovatelId, duvod }: { schvalovatelId: string; duvod: string }) =>
      api<void>(`/operativy/${id}/zamitnout`, {
        method: 'POST',
        body: JSON.stringify({ schvalovatelId, duvod }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: detailKey });
      queryClient.invalidateQueries({ queryKey: schvaleniKey });
      queryClient.invalidateQueries({ queryKey: ['operativy'] });
      toast({ title: 'Zamítnuto' });
      setZamitState(null);
    },
    onError: (err: Error) =>
      toast({ title: 'Zamítnutí selhalo', description: err.message, variant: 'destructive' }),
  });

  // --- Polozky dialog ---
  const [polozkaDialog, setPolozkaDialog] = useState<PolozkaDialogState>({ mode: 'closed' });

  const createPolozkaMutation = useMutation({
    mutationFn: (input: CreatePolozkaInput) =>
      api<Polozka>(`/operativy/${id}/polozky`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: detailKey });
      toast({ title: 'Položka přidána' });
      setPolozkaDialog({ mode: 'closed' });
    },
    onError: (err: Error) =>
      toast({ title: 'Přidání selhalo', description: err.message, variant: 'destructive' }),
  });

  const updatePolozkaMutation = useMutation({
    mutationFn: ({ polozkaId, input }: { polozkaId: string; input: UpdatePolozkaInput }) =>
      api<Polozka>(`/polozky/${polozkaId}`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: detailKey });
      toast({ title: 'Položka upravena' });
      setPolozkaDialog({ mode: 'closed' });
    },
    onError: (err: Error) =>
      toast({ title: 'Úprava selhala', description: err.message, variant: 'destructive' }),
  });

  const deletePolozkaMutation = useMutation({
    mutationFn: (polozkaId: string) =>
      api<void>(`/polozky/${polozkaId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: detailKey });
      toast({ title: 'Položka smazána' });
      setPolozkaDialog({ mode: 'closed' });
    },
    onError: (err: Error) =>
      toast({ title: 'Smazání selhalo', description: err.message, variant: 'destructive' }),
  });

  if (detailQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (detailQuery.isError) {
    const err = detailQuery.error as Error;
    return (
      <div className="space-y-4">
        <Button variant="ghost" asChild>
          <Link to="/operativy">
            <ArrowLeft className="mr-2 h-4 w-4" /> Zpět na seznam
          </Link>
        </Button>
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          Načtení selhalo: {err.message}
        </div>
      </div>
    );
  }

  if (!op) return null;

  // Gating dle role + stavu — viz lib/permissions.ts (zdroj pravdy).
  const canDeleteOp = perms.canSmazatOperativu(op.stav);
  const canEditPolozky = perms.canEditNavrh(op.stav);
  // Po schvaleni jde u polozky menit mechanizace a zamestnanci bez noveho schvaleni;
  // popis cinnosti je zamceny (jeho zmena = Otevrit k uprave + nove schvaleni).
  const canUpravitPoSchvaleni = perms.canUpravitPoSchvaleni(op.stav);
  // Poznamka: VEDOUCI/ADMIN (NAVRH/UPRAVENA) nebo TECHNIK_EXT behem realizace.
  // Backend kryje policy CanEditPoznamka + OperativaService.MuzeEditovatPoznamku.
  const canEditPoznamka = perms.canEditPoznamka(op.stav);
  const canOdeslat = perms.canOdeslatKeSchvaleni(op.stav);
  const canOtevrit = perms.canOtevritKUprave(op.stav);
  const canUzavrit = perms.canUzavritDen(op.stav);

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/operativy">
          <ArrowLeft className="mr-2 h-4 w-4" /> Zpět na seznam
        </Link>
      </Button>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-semibold">{formatDateCs(op.datum)}</h2>
            <StavBadge stav={op.stav} type="operativa" />
          </div>
          <div className="text-sm text-muted-foreground">
            Vytvořil: <strong>{op.vytvorilJmeno}</strong> dne {formatDateTimeCs(op.vytvoreno)}
            {op.upraveno && (
              <>
                {' '}
                • Upraveno: {formatDateTimeCs(op.upraveno)}
              </>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {canOdeslat && (
            <Button
              variant="default"
              size="sm"
              disabled={odeslatMutation.isPending}
              onClick={() => odeslatMutation.mutate()}
              title="Odeslat operativu ke schvalovatelům"
            >
              {odeslatMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              Odeslat ke schválení
            </Button>
          )}
          {canOtevrit && (
            <Button
              variant="outline"
              size="sm"
              disabled={otevritMutation.isPending}
              onClick={() => otevritMutation.mutate()}
              title="Vrátit operativu k úpravě kvůli změně popisu činností (zruší schválení). Materiál, report, mechanizaci a zaměstnance lze měnit i bez toho."
            >
              {otevritMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="mr-2 h-4 w-4" />
              )}
              Otevřít k úpravě
            </Button>
          )}
          {canUzavrit && (
            <Button
              variant="outline"
              size="sm"
              disabled={uzavritMutation.isPending}
              onClick={() => uzavritMutation.mutate()}
              title="Uzavřít den (po dokončení reportů)"
            >
              {uzavritMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Lock className="mr-2 h-4 w-4" />
              )}
              Uzavřít den
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            title="Stáhnout denní operativu jako PDF"
            onClick={() => window.open(`/api/operativy/${op.id}/pdf`, '_blank')}
          >
            <FileText className="mr-2 h-4 w-4" />
            PDF
          </Button>
          {canDeleteOp && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Smazat
            </Button>
          )}
        </div>
      </div>

      {/* Poznamka */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Poznámka</CardTitle>
          {!poznamkaEditing && canEditPoznamka && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setPoznamkaDraft(op.poznamka ?? '');
                setPoznamkaEditing(true);
              }}
            >
              <Pencil className="mr-2 h-4 w-4" />
              {op.poznamka ? 'Upravit' : 'Přidat'}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {poznamkaEditing ? (
            <div className="space-y-3">
              <RichText
                value={poznamkaDraft}
                onChange={setPoznamkaDraft}
                minHeightClass="min-h-[6rem]"
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  disabled={poznamkaMutation.isPending}
                  onClick={() => poznamkaMutation.mutate(richKUlozeni(poznamkaDraft))}
                >
                  {poznamkaMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Uložit'
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPoznamkaEditing(false)}
                  disabled={poznamkaMutation.isPending}
                >
                  Zrušit
                </Button>
              </div>
            </div>
          ) : op.poznamka ? (
            <RichTextView value={op.poznamka} />
          ) : (
            <p className="text-sm text-muted-foreground">Žádná poznámka.</p>
          )}
        </CardContent>
      </Card>

      {/* Schvalovani — viditelne kdyz uz existuji Schvaleni radky */}
      {(schvaleniQuery.data?.length ?? 0) > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Schvalování</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Schvalovatel</TableHead>
                  <TableHead className="w-32">Stav</TableHead>
                  <TableHead>Rozhodnuto</TableHead>
                  <TableHead className="w-60 text-right">Akce</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {schvaleniQuery.data!.map((s) => {
                  const stavLabel =
                    s.stav === 'CEKA'
                      ? 'Čeká'
                      : s.stav === 'SCHVALIL'
                        ? 'Schválil'
                        : 'Zamítl';
                  // Akce nabidneme jen na radku, o kterem prihlaseny uzivatel SMI
                  // rozhodnout (vlastni radek nebo zastup) — `muzeRozhodnout` pocita
                  // server (zrcadlo OveritOpravneni). Driv se tlacitka ukazovala kazdemu
                  // schvalovateli i u cizich radku a klik koncil 403.
                  const showActions =
                    perms.canSchvalovat(op.stav) && s.stav === 'CEKA' && s.muzeRozhodnout;
                  return (
                    <TableRow key={s.id}>
                      <TableCell className="font-medium">{s.schvalovatelJmeno}</TableCell>
                      <TableCell>{stavLabel}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {s.rozhodnuto ? (
                          <>
                            {formatDateTimeCs(s.rozhodnuto)}
                            {s.rozhodlJmeno && s.rozhodlJmeno !== s.schvalovatelJmeno && (
                              <span className="ml-1 text-xs">(za {s.rozhodlJmeno})</span>
                            )}
                            {s.duvodZamitnuti && (
                              <div className="mt-1 text-xs italic">
                                Důvod: {s.duvodZamitnuti}
                              </div>
                            )}
                          </>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {showActions ? (
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={schvalitMutation.isPending}
                              onClick={() =>
                                setSchvalitState({
                                  schvalovatelId: s.schvalovatelId,
                                  // Predvyber = uz drive vybrani ucastnici (union drzi backend).
                                  vybrani: op.ucastnici.map((u) => u.userId),
                                })
                              }
                              title="Schválit za tohoto schvalovatele"
                            >
                              <CheckCircle2 className="mr-1 h-4 w-4 text-green-600" />
                              Schválit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setZamitState({ schvalovatelId: s.schvalovatelId, duvod: '' })
                              }
                              title="Zamítnout s důvodem"
                            >
                              <XCircle className="mr-1 h-4 w-4 text-destructive" />
                              Zamítnout
                            </Button>
                          </div>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ucastnici operativy — vybrani schvalovatelem pri schvaleni, tisknou se v PDF */}
      {op.ucastnici.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Účastníci operativy</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">
              {op.ucastnici.map((u) => u.jmeno).join(', ')}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Polozky */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Položky ({op.polozky.length})</CardTitle>
          {canEditPolozky && (
            <Button size="sm" onClick={() => setPolozkaDialog({ mode: 'create' })}>
              <Plus className="mr-2 h-4 w-4" />
              Nová položka
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {op.polozky.length === 0 ? (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              Operativa zatím nemá žádné položky.
            </div>
          ) : (
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">#</TableHead>
                  <TableHead className="w-48">Mechanizace</TableHead>
                  <TableHead className="min-w-[180px]">Popis činnosti</TableHead>
                  <TableHead className="w-44">Zaměstnanci</TableHead>
                  <TableHead className="w-32">Stav</TableHead>
                  <TableHead className="min-w-[180px]">Výkon</TableHead>
                  <TableHead className="min-w-[150px]">Materiál</TableHead>
                  {!perms.isExterni && (
                    <TableHead className="min-w-[180px]">Kontrola</TableHead>
                  )}
                  <TableHead className="w-44 text-right">Akce</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {op.polozky.map((p) => {
                  // Klik na "report" bunky otevre detail polozky.
                  const openDetail = () =>
                    setPolozkaDialog({ mode: 'view', polozkaId: p.id });
                  const vykonTrunc = truncate(p.vykonPrace, 50);
                  const materialCount = p.material.length;
                  const kontrolaTrunc = truncate(p.kontrolaText, 50);
                  const kontrolaTooltip = p.kontrolaText
                    ? [p.kontrolovalJmeno, p.kontrolovano && formatDateTimeCs(p.kontrolovano)]
                        .filter(Boolean)
                        .join(' • ') || undefined
                    : undefined;
                  return (
                    <TableRow key={p.id}>
                      <TableCell className="font-mono text-xs tabular-nums">{p.poradi}</TableCell>
                      <TableCell>
                        <div className="font-medium">{p.mechanizaceKod}</div>
                        <div className="text-xs text-muted-foreground">{p.mechanizaceNazev}</div>
                      </TableCell>
                      <TableCell>
                        <RichTextView value={p.popisCinnosti} />
                      </TableCell>
                      <TableCell>{p.osoba ?? '—'}</TableCell>
                      <TableCell>
                        <StavBadge stav={p.stav} type="polozka" />
                      </TableCell>
                      <TableCell
                        className="cursor-pointer text-sm"
                        onClick={openDetail}
                        title={richToPlain(p.vykonPrace)}
                      >
                        {p.vykonPrace ? (
                          <div className="flex items-start gap-1.5">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                            <span>{vykonTrunc}</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell
                        className="cursor-pointer text-sm"
                        onClick={openDetail}
                        title={materialTooltip(p.material)}
                      >
                        {materialCount > 0 ? (
                          <span>
                            {materialCount} {materialCount === 1 ? 'položka' : materialCount < 5 ? 'položky' : 'položek'}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      {!perms.isExterni && (
                        <TableCell
                          className="cursor-pointer text-sm"
                          onClick={openDetail}
                          title={kontrolaTooltip}
                        >
                          {p.kontrolaText ? (
                            <div className="flex items-start gap-1.5">
                              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                              <span>{kontrolaTrunc}</span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      )}
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={openDetail}
                            title="Otevřít detail položky a vyplnit report (výkon, materiál, zjištění, kontrola)"
                          >
                            <Eye className="mr-1.5 h-4 w-4" />
                            Otevřít
                          </Button>
                          {(canEditPolozky || canUpravitPoSchvaleni) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setPolozkaDialog({ mode: 'edit', item: p })}
                              title={
                                canEditPolozky
                                  ? 'Upravit plán položky'
                                  : 'Upravit mechanizaci a zaměstnance (bez nového schválení)'
                              }
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {canEditPolozky && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setPolozkaDialog({ mode: 'delete', item: p })}
                                title="Smazat"
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Polozka form (create/edit) */}
      <PolozkaFormDialog
        state={polozkaDialog}
        onClose={() => setPolozkaDialog({ mode: 'closed' })}
        onSubmit={(input, polozkaId) => {
          if (polozkaId) {
            updatePolozkaMutation.mutate({ polozkaId, input });
          } else {
            createPolozkaMutation.mutate(input);
          }
        }}
        submitting={createPolozkaMutation.isPending || updatePolozkaMutation.isPending}
        popisZamceny={!canEditPolozky}
      />

      {/* Polozka delete confirm */}
      <Dialog
        open={polozkaDialog.mode === 'delete'}
        onOpenChange={(v) => !v && setPolozkaDialog({ mode: 'closed' })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Smazat položku?</DialogTitle>
          </DialogHeader>
          {polozkaDialog.mode === 'delete' && (
            <p className="text-sm text-muted-foreground">
              Opravdu chcete smazat položku #{polozkaDialog.item.poradi} ({polozkaDialog.item.mechanizaceKod})?
              Tato akce je nevratná.
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPolozkaDialog({ mode: 'closed' })}
            >
              Zrušit
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deletePolozkaMutation.isPending}
              onClick={() => {
                if (polozkaDialog.mode === 'delete') {
                  deletePolozkaMutation.mutate(polozkaDialog.item.id);
                }
              }}
            >
              {deletePolozkaMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Smazat'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Polozka detail / report */}
      <PolozkaDetailDialog
        open={polozkaDialog.mode === 'view'}
        polozkaId={polozkaDialog.mode === 'view' ? polozkaDialog.polozkaId : null}
        operativaStav={op.stav}
        onClose={() => setPolozkaDialog({ mode: 'closed' })}
        invalidateOnSuccess={detailKey}
      />

      {/* Confirm smazani operativy */}
      <Dialog open={confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Smazat operativu?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Opravdu chcete smazat operativu na den <strong>{formatDateCs(op.datum)}</strong>?
            Smaže se i všechny její položky. Tato akce je nevratná.
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmDelete(false)}>
              Zrušit
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteOpMutation.isPending}
              onClick={() => deleteOpMutation.mutate()}
            >
              {deleteOpMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Smazat'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schvalit — dialog s vyberem ucastniku operativy */}
      <Dialog
        open={schvalitState !== null}
        onOpenChange={(v) => !v && setSchvalitState(null)}
      >
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Schválit operativu</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Vyberte účastníky operativy — vytisknou se na konci PDF záznamu. Výběr je
              nepovinný.
            </p>
            {uzivateleVyberQuery.isLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border p-2">
                {(uzivateleVyberQuery.data ?? []).map((u) => (
                  <label
                    key={u.id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-accent"
                  >
                    <input
                      type="checkbox"
                      checked={schvalitState?.vybrani.includes(u.id) ?? false}
                      onChange={(e) =>
                        setSchvalitState((prev) => {
                          if (!prev) return prev;
                          const vybrani = e.target.checked
                            ? [...prev.vybrani, u.id]
                            : prev.vybrani.filter((x) => x !== u.id);
                          return { ...prev, vybrani };
                        })
                      }
                      className="h-4 w-4 rounded border-input"
                    />
                    <span>{u.jmeno}</span>
                    <span className="text-xs text-muted-foreground">
                      {u.strana === 'NASE_FIRMA' ? 'Green Gas DPB' : 'TPS'}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setSchvalitState(null)}>
              Zrušit
            </Button>
            <Button
              type="button"
              disabled={schvalitMutation.isPending}
              onClick={() => {
                if (!schvalitState) return;
                schvalitMutation.mutate({
                  schvalovatelId: schvalitState.schvalovatelId,
                  ucastniciIds: schvalitState.vybrani,
                });
              }}
            >
              {schvalitMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Schválit
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Zamitnout schvaleni — dialog s povinnym duvodem */}
      <Dialog
        open={zamitState !== null}
        onOpenChange={(v) => !v && setZamitState(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Zamítnout operativu</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Uveďte důvod zamítnutí. Operativa se vrátí dodavateli k úpravě.
            </p>
            <textarea
              rows={4}
              value={zamitState?.duvod ?? ''}
              onChange={(e) =>
                setZamitState((prev) => (prev ? { ...prev, duvod: e.target.value } : prev))
              }
              placeholder="Důvod zamítnutí (min. 3 znaky)"
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setZamitState(null)}>
              Zrušit
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={
                zamitnoutMutation.isPending || (zamitState?.duvod?.trim().length ?? 0) < 3
              }
              onClick={() => {
                if (!zamitState) return;
                zamitnoutMutation.mutate({
                  schvalovatelId: zamitState.schvalovatelId,
                  duvod: zamitState.duvod.trim(),
                });
              }}
            >
              {zamitnoutMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Zamítnout'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =========================================================================
// Polozka — pridani / uprava planu (mechanizace, popis cinnosti, zamestnanci)
// =========================================================================

/** Ulozeny text osoby ("Jmeno1, Jmeno2" nebo stary volny text) → pole jmen. */
function rozdelOsoby(osoba: string | null | undefined): string[] {
  return (osoba ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

interface PolozkaFormProps {
  state: PolozkaDialogState;
  onClose: () => void;
  onSubmit: (input: CreatePolozkaInput | UpdatePolozkaInput, polozkaId?: string) => void;
  submitting: boolean;
  /** Schvalena operativa — popis cinnosti jen ke cteni (jeho zmena vyzaduje nove schvaleni). */
  popisZamceny: boolean;
}

function PolozkaFormDialog({ state, onClose, onSubmit, submitting, popisZamceny }: PolozkaFormProps) {
  const open = state.mode === 'create' || state.mode === 'edit';
  const editItem = state.mode === 'edit' ? state.item : null;

  const mechanizaceQuery = useQuery<Mechanizace[]>({
    queryKey: ['mechanizace'],
    queryFn: () => api<Mechanizace[]>('/mechanizace'),
    enabled: open,
  });
  // Cinnosti slouzi jako navrhy popisu — uzivatel muze vybrat z ciselniku, nebo
  // si napsat vlastni text.
  const cinnostiQuery = useQuery<Cinnost[]>({
    queryKey: ['cinnosti'],
    queryFn: () => api<Cinnost[]>('/cinnosti'),
    enabled: open,
  });

  const [mechanizaceId, setMechanizaceId] = useState<string>('');
  const [popis, setPopis] = useState<string>('');
  const [osoby, setOsoby] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    if (editItem) {
      setMechanizaceId(editItem.mechanizaceId);
      setPopis(editItem.popisCinnosti);
      // Ulozeny text "Jmeno1, Jmeno2" → pole; stara data s volnym textem
      // zustanou vybrana jako polozky mimo ciselnik.
      setOsoby(rozdelOsoby(editItem.osoba));
    } else {
      setMechanizaceId('');
      setPopis('');
      setOsoby([]);
    }
  }, [open, editItem]);

  const mechanizace = (mechanizaceQuery.data ?? []).filter((m) => m.aktivni || m.id === mechanizaceId);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const popisHtml = richKUlozeni(popis);
    if (!mechanizaceId || popisHtml === null) return;
    onSubmit(
      {
        mechanizaceId,
        popisCinnosti: popisHtml,
        osoba: osoby.length === 0 ? null : osoby.join(', '),
      },
      editItem?.id,
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editItem ? 'Upravit položku' : 'Nová položka'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="polozka-mechanizace">Mechanizace</Label>
            <Select value={mechanizaceId} onValueChange={setMechanizaceId}>
              <SelectTrigger id="polozka-mechanizace">
                <SelectValue placeholder="Vyberte mechanizaci" />
              </SelectTrigger>
              <SelectContent>
                {mechanizace.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.kod} — {m.nazev}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="polozka-popis">Popis činnosti</Label>
            {popisZamceny ? (
              <>
                <div className="rounded-md border bg-muted/30 p-3 text-sm">
                  <RichTextView value={popis} />
                </div>
                <p className="text-xs text-muted-foreground">
                  Operativa je schválená — mechanizaci a zaměstnance můžete změnit hned. Změna
                  popisu činnosti vyžaduje nové schválení (tlačítko „Otevřít k úpravě").
                </p>
              </>
            ) : (
              <RichText
                id="polozka-popis"
                value={popis}
                onChange={setPopis}
                minHeightClass="min-h-[16rem]"
              />
            )}
            {!popisZamceny && (cinnostiQuery.data ?? []).length > 0 && (
              <div className="space-y-1 pt-1">
                <p className="text-xs text-muted-foreground">
                  Předvolby z číselníku — vloží název tučně a pod něj popis činnosti:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {(cinnostiQuery.data ?? [])
                    .filter((c) => c.aktivni)
                    .slice(0, 12)
                    .map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() =>
                          setPopis((p) => richPripoj(p, richBlokSNazvem(c.nazev, c.popis)))
                        }
                        className="rounded-full border bg-muted px-2 py-0.5 text-xs hover:bg-accent"
                        title={[c.nazev, richToPlain(c.popis)].filter(Boolean).join('\n\n')}
                      >
                        {c.kod}
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="polozka-osoba">Zaměstnanci (volitelné)</Label>
            <ZamestnanciMultiSelect id="polozka-osoba" value={osoby} onChange={setOsoby} />
            <p className="text-xs text-muted-foreground">
              Vybírá se z číselníku Zaměstnanci; nové jméno lze přidat přímo napsáním.
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Zrušit
            </Button>
            {/* Popis uz neni <textarea required> — prazdny obsah hlidame tady. */}
            <Button
              type="submit"
              disabled={submitting || !mechanizaceId || jeRichPrazdny(popis)}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Uložit'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
